// src/NavBar.js
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth } from "./firebase";
import "./NavBar.css";
import { puedeVerPanel, esAdmin as esAdminCuenta, esCobros } from "./roles";

function NavBar({ usuario, setUsuario }) {
  const navigate = useNavigate();
  const location = useLocation();
  const esAdmin = puedeVerPanel(usuario); // admin o cobros: ven el PANEL

  const cuentaAdmin = esAdminCuenta(usuario);
  const cuentaCobros = esCobros(usuario);

  // Cuenta con la que se está logueado (se muestra a la derecha en computadora)
  const cuentaLabel = cuentaAdmin
    ? "Admin"
    : cuentaCobros
    ? "Cobros"
    : usuario?.nombre || usuario?.email || "";
  const cuentaClase = cuentaAdmin
    ? "navbar-user navbar-user--admin"
    : cuentaCobros
    ? "navbar-user navbar-user--cobros"
    : "navbar-user";

  // Celular: círculo con iniciales que abre un menú con "Cerrar Sesión"
  const nombreCompleto =
    `${usuario?.nombre || ""} ${usuario?.apellido || ""}`.trim() || usuario?.email || "";
  const iniciales = cuentaAdmin
    ? "A"
    : cuentaCobros
    ? "C"
    : (
        `${(usuario?.nombre || "").trim().charAt(0)}${(usuario?.apellido || "").trim().charAt(0)}` ||
        (usuario?.email || "?").charAt(0)
      ).toUpperCase();
  const avatarClase = cuentaAdmin
    ? "user-avatar user-avatar--admin"
    : cuentaCobros
    ? "user-avatar user-avatar--cobros"
    : "user-avatar";

  const [menuAbierto, setMenuAbierto] = useState(false);
  const [posMenu, setPosMenu] = useState({ top: 56, right: 10 });
  const avatarRef = useRef(null);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    if (!menuAbierto && avatarRef.current) {
      // La barra recorta lo que sobresale, así que el menú se ubica fijo bajo el círculo
      const r = avatarRef.current.getBoundingClientRect();
      setPosMenu({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    }
    setMenuAbierto((v) => !v);
  };

  // Cerrar el menú al tocar afuera, al hacer scroll o al cambiar de página
  useEffect(() => {
    if (!menuAbierto) return;
    const cerrarSiAfuera = (e) => {
      if (menuRef.current?.contains(e.target) || avatarRef.current?.contains(e.target)) return;
      setMenuAbierto(false);
    };
    const cerrar = () => setMenuAbierto(false);
    document.addEventListener("mousedown", cerrarSiAfuera);
    document.addEventListener("touchstart", cerrarSiAfuera);
    window.addEventListener("scroll", cerrar, { passive: true });
    window.addEventListener("resize", cerrar);
    return () => {
      document.removeEventListener("mousedown", cerrarSiAfuera);
      document.removeEventListener("touchstart", cerrarSiAfuera);
      window.removeEventListener("scroll", cerrar);
      window.removeEventListener("resize", cerrar);
    };
  }, [menuAbierto]);

  useEffect(() => {
    setMenuAbierto(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setMenuAbierto(false);
    auth.signOut().then(() => {
      setUsuario(null);
      navigate("/login");
    });
  };

  // Helper para renderizar cada item con icono+texto
  const NavItem = ({ to, icon, text, className = "" }) => (
    <Link to={to} className={`navbar-link ${className}`} aria-label={text}>
      {icon ? <span className="nav-ico" aria-hidden="true">{icon}</span> : null}
      <span className="nav-text">{text}</span>
    </Link>
  );

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <NavItem to="/" text="Inicio" className="inicio-link" />

        {usuario && (
          <>
            {/* Casas (misma ruta /reservas) */}
            <NavItem to="/reservas" icon="🏡" text="Casas" />
            <NavItem to="/camping"  icon="🏕️" text="Camping" />
            <NavItem to="/dia"      icon="🌞" text="Día" />

            {/* PANEL va antes que Info Útil para asegurar visibilidad en móvil */}
            {esAdmin && (
              <NavItem to="/admin" icon="🛠️" text="PANEL" className="navbar-link--admin" />
            )}

            {/* Ocultar Info Útil solo en móvil cuando es admin */}
            <NavItem
              to="/info"
              icon="ℹ️"
              text="Info Útil"
              className={esAdmin ? "hide-admin-mobile" : ""}
            />
          </>
        )}
      </div>

      <div className="navbar-right">
        {usuario ? (
          <>
            {/* Computadora: etiqueta de cuenta + botón */}
            <span className={`${cuentaClase} solo-desktop`} title={usuario.email}>
              👤 {cuentaLabel}
            </span>
            <button onClick={handleLogout} className="navbar-button solo-desktop">
              Cerrar Sesión
            </button>

            {/* Celular: iniciales que abren el menú de usuario */}
            <button
              ref={avatarRef}
              type="button"
              className={`${avatarClase} solo-movil`}
              onClick={toggleMenu}
              aria-label="Menú de usuario"
              aria-expanded={menuAbierto}
            >
              {iniciales}
            </button>
          </>
        ) : (
          <Link to="/login" className="navbar-link">Iniciar Sesión</Link>
        )}
      </div>

      {usuario && menuAbierto && (
        <div
          ref={menuRef}
          className="user-dropdown"
          role="menu"
          style={{ top: posMenu.top, right: posMenu.right }}
        >
          <div className="user-dropdown-nombre">{nombreCompleto}</div>
          {(cuentaAdmin || cuentaCobros) && (
            <div className="user-dropdown-cuenta">
              Cuenta: <b>{cuentaAdmin ? "Admin" : "Cobros"}</b>
            </div>
          )}
          <div className="user-dropdown-email">{usuario.email}</div>
          <button onClick={handleLogout} className="navbar-button user-dropdown-salir">
            Cerrar Sesión
          </button>
        </div>
      )}
    </nav>
  );
}

export default NavBar;
